/**
 * TextInput — Themed input with focus ring, error state, and icon support.
 */

import { forwardRef, useState } from 'react'
import { Eye, EyeOff }          from 'lucide-react'

export const TextInput = forwardRef(function TextInput(
  { type = 'text', placeholder, error, icon: Icon, className, ...props },
  ref
) {
  const [showPassword, setShowPassword] = useState(false)
  const isPassword = type === 'password'
  const inputType  = isPassword ? (showPassword ? 'text' : 'password') : type

  return (
    <div className="relative">
      {/* Left icon */}
      {Icon && (
        <div
          className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none"
          style={{ color: error ? 'var(--ac-danger)' : 'var(--ac-text-muted)' }}
        >
          <Icon size={16} strokeWidth={1.75} />
        </div>
      )}

      <input
        ref={ref}
        type={inputType}
        placeholder={placeholder}
        aria-invalid={!!error}
        className={[
          'w-full h-11 rounded-xl text-sm transition-all duration-200 outline-none',
          'bg-[var(--ac-bg-subtle)] border',
          'placeholder:text-[var(--ac-text-subtle)]',
          'text-[var(--ac-text-base)]',
          Icon         ? 'pl-10' : 'pl-3.5',
          isPassword   ? 'pr-11' : 'pr-3.5',
          error
            ? 'border-[var(--ac-danger)] focus:ring-2 focus:ring-[var(--ac-danger)]/20 focus:border-[var(--ac-danger)]'
            : 'border-[var(--ac-border-base)] focus:ring-2 focus:ring-[var(--ac-secondary)]/20 focus:border-[var(--ac-border-focus)]',
          className,
        ].filter(Boolean).join(' ')}
        {...props}
      />

      {/* Password toggle */}
      {isPassword && (
        <button
          type="button"
          tabIndex={-1}
          onClick={() => setShowPassword((v) => !v)}
          className="absolute right-3.5 top-1/2 -translate-y-1/2 transition-colors duration-150 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ac-secondary)]"
          style={{ color: 'var(--ac-text-subtle)' }}
          aria-label={showPassword ? 'Hide password' : 'Show password'}
        >
          {showPassword
            ? <EyeOff size={16} strokeWidth={1.75} />
            : <Eye    size={16} strokeWidth={1.75} />
          }
        </button>
      )}
    </div>
  )
})
